import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import PasswordInput from './PasswordInput';
import { useAuth } from '../contexts/AuthProvider';
import './StytchLogin.css';

const StytchLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authMethod, setAuthMethod] = useState('oauth'); // 'oauth' or 'magic-link'
  const [step, setStep] = useState('input'); // 'input', 'success', or 'password-reset'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [mfaState, setMfaState] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [isMfaLoading, setIsMfaLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Enhanced JWT Authentication Integration
  const { login, completeMfa, sendMfaChallenge } = useAuth();

  // Check for session expiration reason in URL
  useEffect(() => {
    const reason = searchParams.get('reason');
    if (reason === 'account_deactivated') {
      setError('Your account has been deactivated. Please contact your administrator.');
    } else if (reason === 'session_expired' || reason === 'token_expired') {
      setError('Your session has expired. Please log in again.');
    }
  }, [searchParams]);

  const determineMfaMethod = (payload = {}) => {
    const secondary = payload?.mfaRequired?.secondary_auth_initiated;
    if (secondary === 'sms_otp' || secondary === 'totp') {
      return secondary;
    }
    if (payload?.mfaRequired?.member_options?.mfa_phone) {
      return 'sms_otp';
    }
    return 'totp';
  };

  const maskPhoneNumber = (phone) => {
    if (!phone) return null;
    const digits = phone.toString();
    const visible = digits.slice(-4);
    return `${'•'.repeat(Math.max(0, digits.length - 4))}${visible}`;
  };

  const handleOAuthLogin = async (provider) => {
    setIsLoading(true);
    setError('');

    try {
      // Get OAuth URL from backend
      const response = await apiClient.post('/auth/oauth/url', {
        provider: provider,
        redirect_url: `${window.location.origin}/auth/callback`
      });

      if (response.data.success) {
        // Redirect to OAuth provider
        window.location.href = response.data.authorization_url;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || `Failed to initiate ${provider} login`;
      setError(Array.isArray(errorMessage) ? errorMessage.map(e => e.msg || e).join(', ') : errorMessage);
      setIsLoading(false);
    }
  };

  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/auth/stytch/magic-link', {
        email: email,
        redirect_url: `${window.location.origin}/auth/callback`
      });

      if (response.data.success) {
        setSuccessMessage('Magic link sent! Please check your email and click the link to sign in.');
        setStep('success');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to send magic link';
      setError(Array.isArray(errorMessage) ? errorMessage.map(e => e.msg || e).join(', ') : errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaChallengeRequest = async (methodOverride) => {
    if (!mfaState) {
      return;
    }

    const targetMethod = methodOverride || mfaState.method;
    setIsMfaLoading(true);
    setError('');

    try {
      const challenge = await sendMfaChallenge({
        method: targetMethod,
        organizationId: mfaState.organizationId,
        memberId: mfaState.memberId,
        phoneNumber: mfaState.mfaRequired?.member_options?.mfa_phone,
        intermediateSessionToken: mfaState.intermediateSessionToken,
        sessionToken: mfaState.sessionToken,
        sessionJwt: mfaState.sessionJwt,
        totpRegistrationId: mfaState.totpRegistrationId
      });

      if (!challenge.success) {
        setError(challenge.error || 'Failed to send verification code');
        return;
      }

      const data = challenge.data || {};

      if (targetMethod === 'sms_otp') {
        const phone = mfaState.mfaRequired?.member_options?.mfa_phone;
        const masked = maskPhoneNumber(phone) || mfaState.maskedPhone;
        setMfaState(prev => ({
          ...prev,
          maskedPhone: masked || prev?.maskedPhone || null
        }));
        setSuccessMessage(
          data.message ||
          (masked ? `Verification code sent to ${masked}` : 'Verification code sent via SMS.')
        );
      } else if (targetMethod === 'totp') {
        setMfaState(prev => ({
          ...prev,
          qrCode: data.qr_code || prev?.qrCode || null,
          totpRegistrationId: data.totp_registration_id || prev?.totpRegistrationId || null
        }));
        setSuccessMessage('Scan the QR code with your authenticator app, then enter the code below.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to send verification code';
      setError(Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : detail);
    } finally {
      setIsMfaLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaState || !mfaCode) {
      return;
    }

    setIsMfaLoading(true);
    setError('');

    try {
      const result = await completeMfa({
        method: mfaState.method,
        code: mfaCode.trim(),
        organizationId: mfaState.organizationId,
        memberId: mfaState.memberId,
        intermediateSessionToken: mfaState.intermediateSessionToken,
        sessionToken: mfaState.sessionToken,
        sessionJwt: mfaState.sessionJwt,
        totpRegistrationId: mfaState.totpRegistrationId,
        email: mfaState.email,
        rememberMe: mfaState.rememberMe
      });

      if (result.success) {
        setSuccessMessage('');
        navigate('/calendar');
        return;
      }

      setError(result.error || 'Verification failed');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Verification failed';
      setError(Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : detail);
    } finally {
      setIsMfaLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Use enhanced AuthProvider login method
      const result = await login(email, password, false);

      if (result.success) {
        navigate('/calendar');
        return;
      }

      if (result.mfaRequired) {
        const payload = result.data || {};
        const method = determineMfaMethod(payload);
        const maskedPhone = maskPhoneNumber(payload?.mfaRequired?.member_options?.mfa_phone);

        const baseState = {
          ...payload,
          method,
          maskedPhone,
          secondaryAuthInitiated: payload?.mfaRequired?.secondary_auth_initiated || null,
          qrCode: null,
          totpRegistrationId: payload?.totpRegistrationId || null,
          sessionToken: payload?.sessionToken || null,
          sessionJwt: payload?.sessionJwt || null,
          email,
          rememberMe: false
        };

        setMfaState(baseState);
        setMfaCode('');
        setStep('mfa');

        if (method === 'sms_otp') {
          const message = maskedPhone
            ? `Verification code sent to ${maskedPhone}`
            : 'Verification code sent via SMS.';
          setSuccessMessage(message);
        } else if (method === 'totp') {
          if (payload?.mfaRequired?.member_options) {
            setSuccessMessage('Enter the code from your authenticator app to continue.');
          } else {
            setIsMfaLoading(true);
            try {
              const challenge = await sendMfaChallenge({
                method: 'totp',
                organizationId: payload.organizationId,
                memberId: payload.memberId,
                intermediateSessionToken: payload.intermediateSessionToken
              });

              if (challenge.success) {
                const data = challenge.data || {};
                setMfaState(prev => ({
                  ...prev,
                  qrCode: data.qr_code || prev?.qrCode || null,
                  totpRegistrationId: data.totp_registration_id || prev?.totpRegistrationId || null
                }));
                setSuccessMessage('Scan the QR code with your authenticator app, then enter the code below.');
              } else if (challenge.error) {
                setError(challenge.error);
              }
            } catch (challengeErr) {
              const detail = challengeErr.response?.data?.detail || challengeErr.message || 'Failed to start authenticator setup';
              setError(Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : detail);
            } finally {
              setIsMfaLoading(false);
            }
          }
        } else {
          setSuccessMessage('');
        }

        return;
      }

      setError(result.error || 'Invalid email or password');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Login failed';
      setError(Array.isArray(errorMessage) ? errorMessage.map(e => e.msg || e).join(', ') : errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/auth/stytch/password/reset', {
        email: email,
        redirect_url: `${window.location.origin}/auth/callback`
      });

      if (response.data.success) {
        setSuccessMessage('Password reset instructions sent! Please check your email.');
        setStep('password-reset');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to send password reset email';
      setError(Array.isArray(errorMessage) ? errorMessage.map(e => e.msg || e).join(', ') : errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setStep('input');
    setError('');
    setSuccessMessage('');
    setEmail('');
    setPassword('');
    setMfaCode('');
    setMfaState(null);
    setIsMfaLoading(false);
  };

  return (
    <div className="stytch-login-container">
      <div className="stytch-login-wrapper">
        <div className="stytch-login-header">
          <h1>MedInsight PRO</h1>
          <p>The medical resilience platform</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="stytch-alert stytch-alert-error">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="stytch-alert stytch-alert-success">
            <i className="fas fa-check-circle"></i>
            {successMessage}
          </div>
        )}

        {/* Magic Link Success State */}
        {step === 'success' && (
          <div className="stytch-auth-success">
            <i className="fas fa-envelope-open"></i>
            <h3>Magic Link Sent!</h3>
            <p>We've sent a secure login link to <strong>{email}</strong></p>
            <p>Please check your email and click the link to continue.</p>
            <button
              className="stytch-btn stytch-btn-secondary stytch-btn-full"
              onClick={resetForm}
            >
              Try Different Method
            </button>
          </div>
        )}

        {/* Password Reset Success State */}
        {step === 'password-reset' && (
          <div className="stytch-auth-success">
            <i className="fas fa-key"></i>
            <h3>Password Reset Sent!</h3>
            <p>We've sent password reset instructions to <strong>{email}</strong></p>
            <p>Please check your email and follow the instructions to reset your password.</p>
            <button
              className="stytch-btn stytch-btn-secondary stytch-btn-full"
              onClick={resetForm}
            >
              Try Different Method
            </button>
          </div>
        )}

        {/* MFA Challenge State */}
        {step === 'mfa' && (
          <div className="stytch-login-form">
            <div className="stytch-auth-success">
              <i className="fas fa-shield-alt"></i>
              <h3>Two-step Verification</h3>
              <p>We just need a quick extra check to keep your account secure.</p>
              {mfaState?.method === 'sms_otp' && (
                <p className="stytch-reset-hint">
                  Enter the 6-digit code sent to{' '}
                  <strong>{mfaState?.maskedPhone || 'your phone'}</strong>.
                </p>
              )}
              {mfaState?.method === 'totp' && (
                <p className="stytch-reset-hint">
                  Open your authenticator app and enter the current verification code.
                </p>
              )}
              {mfaState?.qrCode && (
                <div style={{ margin: '20px auto', maxWidth: 220 }}>
                  <img
                    src={mfaState.qrCode}
                    alt="Authenticator QR code"
                    style={{ display: 'block', width: '100%', height: 'auto' }}
                  />
                </div>
              )}
            </div>

            <form onSubmit={handleMfaSubmit}>
              <div className="stytch-form-group">
                <label>
                  <i className="fas fa-lock"></i>
                  Verification Code
                </label>
                <input
                  type="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="Enter your verification code"
                  required
                  disabled={isMfaLoading}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="stytch-btn stytch-btn-primary stytch-btn-full"
                disabled={isMfaLoading || !mfaCode}
              >
                {isMfaLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Verifying...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle"></i>
                    Verify & Continue
                  </>
                )}
              </button>
            </form>

            <div className="stytch-password-reset">
              {mfaState?.method === 'sms_otp' && (
                <button
                  type="button"
                  className="stytch-reset-link"
                  onClick={() => handleMfaChallengeRequest('sms_otp')}
                  disabled={isMfaLoading}
                >
                  <i className="fas fa-sync-alt"></i>
                  Resend code
                </button>
              )}
              {mfaState?.method === 'totp' && !mfaState?.qrCode && (
                <button
                  type="button"
                  className="stytch-reset-link"
                  onClick={() => handleMfaChallengeRequest('totp')}
                  disabled={isMfaLoading}
                >
                  <i className="fas fa-qrcode"></i>
                  Generate QR code
                </button>
              )}
            </div>

            <button
              type="button"
              className="stytch-btn stytch-btn-secondary stytch-btn-full"
              onClick={resetForm}
              disabled={isMfaLoading}
            >
              <i className="fas fa-arrow-left"></i>
              Try a Different Method
            </button>
          </div>
        )}

        {/* Main Login Form */}
        {step === 'input' && (
          <div className="stytch-login-form">

            {/* Auth Method Selector */}
            <div className="stytch-auth-tabs">
              <button
                className={`stytch-auth-tab ${authMethod === 'oauth' ? 'active' : ''}`}
                onClick={() => setAuthMethod('oauth')}
              >
                <i className="fas fa-users"></i>
                Social Login
              </button>
              <button
                className={`stytch-auth-tab ${authMethod === 'magic-link' ? 'active' : ''}`}
                onClick={() => setAuthMethod('magic-link')}
              >
                <i className="fas fa-magic"></i>
                Magic Link
              </button>
            </div>

            {/* OAuth Section */}
            {authMethod === 'oauth' && (
              <div className="stytch-oauth-section">
                <div className="stytch-oauth-buttons">
                  <button
                    className="stytch-oauth-btn google"
                    onClick={() => handleOAuthLogin('google')}
                    disabled={isLoading}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </button>

                  {/* Microsoft and Apple OAuth temporarily hidden for initial deployment
                  <button
                    className="stytch-oauth-btn microsoft"
                    onClick={() => handleOAuthLogin('microsoft')}
                    disabled={isLoading}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#f25022" d="M1 1h10v10H1z"/>
                      <path fill="#00a4ef" d="M13 1h10v10H13z"/>
                      <path fill="#7fba00" d="M1 13h10v10H1z"/>
                      <path fill="#ffb900" d="M13 13h10v10H13z"/>
                    </svg>
                    Continue with Microsoft
                  </button>

                  <button
                    className="stytch-oauth-btn apple"
                    onClick={() => handleOAuthLogin('apple')}
                    disabled={isLoading}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#000">
                      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                    </svg>
                    Continue with Apple
                  </button>
                  */}
                </div>

                <div className="stytch-divider">
                  <span>or</span>
                </div>

                {/* Stytch Password Form */}
                <form onSubmit={handlePasswordSubmit}>
                  <div className="stytch-form-group">
                    <label>
                      <i className="fas fa-envelope"></i>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="Enter your email"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="stytch-form-group">
                    <label>
                      <i className="fas fa-lock"></i>
                      Password
                    </label>
                    <PasswordInput
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="Enter your password"
                      disabled={isLoading}
                      className="stytch-form-group"
                      autoComplete="current-password"
                    />
                  </div>

                  <button
                    type="submit"
                    className="stytch-btn stytch-btn-primary stytch-btn-full"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Signing In...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sign-in-alt"></i>
                        Sign In
                      </>
                    )}
                  </button>
                </form>

                {/* Password Reset Link */}
                <div className="stytch-password-reset">
                  <button
                    type="button"
                    className="stytch-reset-link"
                    onClick={handlePasswordReset}
                    disabled={isLoading || !email}
                  >
                    <i className="fas fa-key"></i>
                    Forgot your password?
                  </button>
                  {!email && (
                    <p className="stytch-reset-hint">
                      Enter your email address above to reset your password
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Magic Link Form */}
            {authMethod === 'magic-link' && (
              <form onSubmit={handleMagicLinkSubmit}>
                <div className="stytch-form-group">
                  <label>
                    <i className="fas fa-envelope"></i>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="submit"
                  className="stytch-btn stytch-btn-primary stytch-btn-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-magic"></i>
                      Send Magic Link
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StytchLogin; 
